"use client"

import clsx from "clsx"
import * as React from "react"
import { useEffect, useRef, useState } from "react"

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover"

import IconAlert from "../global/icons/IconAlert"
import IconChevronDown from "../global/icons/IconChevronDown"

const extractSearchableText = (node: React.ReactNode): string => {
  if (node === null || node === undefined) return ""

  if (typeof node === "string" || typeof node === "number") {
    return String(node)
  }

  if (React.isValidElement(node)) {
    const element = node as React.ReactElement<{
      children?: React.ReactNode
    }>
    return extractSearchableText(element.props.children)
  }

  if (Array.isArray(node)) {
    return node.map(extractSearchableText).join(" ")
  }

  return ""
}

interface ComboboxProps {
  options: Array<{
    value: string
    label: string
    triggerTemplate?: React.ReactNode
    dropdownTemplate?: React.ReactNode
    disabled?: boolean
  }>
  displayChevron?: boolean
  alertText?: string
  displayAlertText?: boolean
  placeholder?: string
  noResultsMessage?: string
  value?: string
  onChange?: (value: string) => void
  renderItem?: (option: { value: string; label: string }) => React.ReactNode
  children?: React.ReactNode
  className?: string
  selectTrigger?: React.ReactElement<{
    "aria-expanded"?: boolean
    onClick?: () => void
  }>
  inline?: boolean
}

const Combobox: React.FC<ComboboxProps> = ({
  options = [],
  placeholder = "Search...",
  noResultsMessage = "No results found.",
  value,
  onChange,
  renderItem,
  alertText,
  displayAlertText = false,
  className,
  displayChevron = true,
  inline = false
}) => {
  const commandGroupRef = useRef<HTMLDivElement>(null)
  const [triggerWidth, setTriggerWidth] = useState(0)
  const triggerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [selectedValue, setSelectedValue] = useState(value)
  // Initialize to empty string for consistent SSR hydration
  // The useEffect below will populate the actual content after mount
  const [triggerContent, setTriggerContent] = useState<
    string | undefined | React.ReactNode
  >("")

  const hasSelectableOptions = options.some(
    (option) =>
      option.disabled !== true && !String(option.value).startsWith("__")
  )

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value)
    }
  }, [value])

  const displaySearch = options.length > 10
  displayAlertText = displayAlertText || displaySearch

  useEffect(() => {
    if (triggerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setTriggerWidth(entry.contentRect.width)
        }
      })

      resizeObserver.observe(triggerRef.current)
      return () => resizeObserver.disconnect()
    }
  }, [])

  // Ensure inline dropdowns are closed by default on mount or when switching to inline mode
  useEffect(() => {
    if (inline) {
      setOpen(false)
    }
  }, [inline])

  useEffect(() => {
    if (open && !hasSelectableOptions) {
      setOpen(false)
    }
  }, [open, hasSelectableOptions])

  const handleSelect = (selectedValue: string) => {
    setSelectedValue(selectedValue)
    setOpen(false)

    if (onChange) {
      onChange(selectedValue)
    }
  }

  const filteredOptions = options.filter((option) => {
    if (!React.isValidElement(option.dropdownTemplate)) return false

    const { children: childText } = option.dropdownTemplate.props as {
      children?: React.ReactNode
    }

    if (!searchValue) return true

    const searchableText = extractSearchableText(childText).toLowerCase()
    const searchTerm = searchValue.toLowerCase()

    return (
      option.label.toLowerCase().includes(searchTerm) ||
      searchableText.includes(searchTerm)
    )
  })

  useEffect(() => {
    const selectedOption = options.find(
      (option) => option.value === selectedValue
    )
    setTriggerContent(selectedOption?.triggerTemplate || "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue, value])

  useEffect(() => {
    if (open && commandGroupRef.current && selectedValue) {
      setTimeout(() => {
        const selectedItem = commandGroupRef.current?.querySelector(
          `[data-value="${selectedValue}"]`
        ) as HTMLElement

        if (selectedItem) {
          selectedItem.focus()
        }
      }, 0)
    }
  }, [open, selectedValue])

  if (inline) {
    return (
      <div className={className}>
        <div className="relative z-20 isolate w-full rounded-md border bg-white shadow-sm">
          <button
            type="button"
            className={clsx(
              "w-full flex flex-row h-16 items-center rounded-md px-3 py-2 bg-white justify-between",
              {
                "border-red-500": className?.includes("text-red")
              }
            )}
            onClick={() => {
              if (!hasSelectableOptions) return
              setOpen((prev) => !prev)
            }}>
            {triggerContent || placeholder}
            {displayChevron && (
              <IconChevronDown
                className={clsx("w-6", {
                  "transform rotate-180": open
                })}
              />
            )}
          </button>
          {open && (
            <>
              <Command shouldFilter={false}>
                {displaySearch && (
                  <CommandInput
                    placeholder={placeholder}
                    value={searchValue}
                    onValueChange={setSearchValue}
                  />
                )}
                <CommandList
                  className="p-0 max-h-[200px]"
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}>
                  {filteredOptions.length > 0 ? (
                    <CommandGroup className="p-0" ref={commandGroupRef}>
                      {filteredOptions.map((option) =>
                        renderItem ? (
                          renderItem(option)
                        ) : (
                          <CommandItem
                            tabIndex={option.disabled ? -1 : 0}
                            aria-disabled={option.disabled || undefined}
                            className={clsx("combobox-item", {
                              "selected-combobox-item bg-purple-200":
                                option.value === selectedValue,
                              "opacity-60 pointer-events-none": option.disabled
                            })}
                            key={option.value}
                            value={option.value}
                            onSelect={
                              option.disabled
                                ? undefined
                                : () => handleSelect(option.value)
                            }>
                            {option.dropdownTemplate || option.label}
                          </CommandItem>
                        )
                      )}
                    </CommandGroup>
                  ) : null}
                </CommandList>
              </Command>
              {displayAlertText && (
                <div className="flex gap-2 p-4 text-sm bg-red-200 rounded-lg m-4">
                  <span className="text-purple">
                    <IconAlert className="min-w-6 w-6" color="currentColor" />
                  </span>
                  <span className="mt-1">
                    {filteredOptions?.length === 0
                      ? noResultsMessage
                      : alertText}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const handleOpenChange = (next: boolean) => {
    if (!hasSelectableOptions) return
    setOpen(next)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild className={className}>
        <div ref={triggerRef}>
          {options.length > 0 ? (
            <div
              className={clsx(
                "flex flex-row min-w-[200px] h-16 items-center rounded-md border px-3 py-2 bg-white shadow-sm justify-between",
                {
                  "border-red-500": className?.includes("text-red")
                }
              )}>
              {triggerContent}
              {displayChevron && (
                <IconChevronDown
                  className={clsx("w-6", {
                    "transform rotate-180": open
                  })}
                />
              )}
            </div>
          ) : (
            <div
              className="flex flex-col min-w-[200px] rounded-md border px-3 py-2 bg-white shadow-sm"
              aria-busy="true"
              aria-live="polite">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-black-200 animate-pulse" />
                <div className="h-3 w-24 rounded bg-black-200 animate-pulse" />
              </div>
              <span className="sr-only">{placeholder || "Laddar..."}</span>
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        style={{ minWidth: `${triggerWidth}px` }}
        className="p-0"
        hasBackground={false}>
        <Command shouldFilter={false}>
          {displaySearch && (
            <CommandInput
              placeholder={placeholder}
              value={searchValue}
              onValueChange={setSearchValue}
            />
          )}
          <CommandList
            className="p-0"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}>
            <>
              {filteredOptions.length > 0 ? (
                <CommandGroup className="p-0" ref={commandGroupRef}>
                  {filteredOptions.map((option) =>
                    renderItem ? (
                      renderItem(option)
                    ) : (
                      <CommandItem
                        tabIndex={option.disabled ? -1 : 0}
                        aria-disabled={option.disabled || undefined}
                        className={clsx("combobox-item", {
                          "selected-combobox-item bg-purple-200":
                            option.value === selectedValue,
                          "opacity-60 pointer-events-none": option.disabled
                        })}
                        key={option.value}
                        value={option.value}
                        onSelect={
                          option.disabled
                            ? undefined
                            : () => handleSelect(option.value)
                        }>
                        {option.dropdownTemplate || option.label}
                      </CommandItem>
                    )
                  )}
                </CommandGroup>
              ) : null}
            </>
          </CommandList>
        </Command>
        {/* Display only no results message (no alert text) */}
        {displayAlertText && filteredOptions?.length === 0 && (
          <div className="flex gap-2 p-4 text-sm bg-red-200 rounded-lg m-4">
            <span className="text-purple">
              <IconAlert className="min-w-6 w-6" color="currentColor" />
            </span>
            <span className="mt-1">
              {filteredOptions?.length === 0 ? noResultsMessage : alertText}
            </span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default Combobox
